import { FC } from 'react'
import { H2 } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import styled from 'styled-components'
import { validFor } from '@/theme/validFor'
import Link from '@/components/atoms/link'
import SearchDropdown from '@/components/molecules/SearchDropdown'
import { Manufacturer as ManufacturerModel } from '@/network/graphql.g'
import ManufacturerList from './manufacturer-list'
import PageContentWrapper, {
  PageContentWrapperBackgroundStyle
} from '../pageContentWrapper'

type ManufacturerWrapperProps = {
  manufacturersList: Record<string, ManufacturerModel[]>
  manufacturers: ManufacturerModel[]
}

const ManufacturerWrapper: FC<ManufacturerWrapperProps> = ({
  manufacturersList,
  manufacturers
}) => {
  const { t } = useTranslation('common')

  return (
    <PageContentWrapper
      mb={96}
      backgroundStyle={PageContentWrapperBackgroundStyle.normal}
    >
      <PageContentWrapperstyled>
        <StyledH2 mr={30}>{t('Manufacturer.titleNew')}</StyledH2>
        <SearchDropdown
          placeholder={t('Manufacturer.searchPlaceholder')}
          manufacturers={[
            {
              items: manufacturers.map((item) => ({
                id: item.id.toString(),
                title: item.name,
                link: item.link || `/manufacturer/${item.id}`
              }))
            }
          ]}
        />
        <AlphabeticalNavigationWrapperStyled>
          <AlphabeticalNavigationWrapper>
            <AlphabeticalNavigation>
              {Object.keys(manufacturersList).map((item) => (
                <AlphabetLink key={item} href={`#${item.replace(/\s/g, '')}`}>
                  <AlphabetLetter>{item}</AlphabetLetter>
                </AlphabetLink>
              ))}
            </AlphabeticalNavigation>
          </AlphabeticalNavigationWrapper>
        </AlphabeticalNavigationWrapperStyled>
        <ManufacturerList manufacturersList={manufacturersList} />
      </PageContentWrapperstyled>
    </PageContentWrapper>
  )
}

export default ManufacturerWrapper

const PageContentWrapperstyled = styled.div`
  ${validFor.desktop`
    margin: 0 185px;
  `}
`

const StyledH2 = styled(H2)`
  margin-bottom: 0;
  ${validFor.mobile`
 
    font-size: 24px;
    font-weight: 700;
    margin: 20px 5px 0 5px;
  `}
`

const AlphabeticalNavigationWrapperStyled = styled.div`
  ${validFor.mobile`
    
    width: calc(100% - 10px);
    margin: 20px 0px -15px 5px;
  `}
`

const AlphabeticalNavigationWrapper = styled.div`
  width: 100%;
  margin-bottom: 40px;

  ${validFor.mobile`
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    margin: 20px 0 30px 0;
    
    &::-webkit-scrollbar {
      display: none;
    }
    
    -ms-overflow-style: none;
    scrollbar-width: none;
    
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 20px;
      background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.8));
      pointer-events: none;
    }
  `}
`

const AlphabeticalNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0;

  ${validFor.mobile`
    width: max-content;
    min-width: 100%;
    justify-content: flex-start;
    gap: 0;
    padding: 0;
  `}
`

const AlphabetLink = styled(Link)`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  position: relative;
  padding: 0;
  height: 40px;

  ${validFor.mobile`
    flex: 0 0 auto;
    min-width: 35px;
    height: 35px;
  `}
`

const AlphabetLetter = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.buttonColorNew};
  text-transform: uppercase;
  transition: all 0.2s ease;
  padding: 2px 8px;

  &:hover {
    border-bottom: 3px solid ${(props) => props.theme.colors.buttonColorNew};
  }

  ${validFor.mobile`
    font-size: 14px;
    padding: 5px 10px;
  `}
`
